import { Command } from "commander";
import { existsSync } from "node:fs";
import { resolve } from "node:path";
import chalk from "chalk";
import { warnAction } from "../ux/errors.js";

export const projectCommand = new Command("project")
  .description("View or update project intent and regenerate projections")
  .option("--regenerate", "Regenerate projection files from current intent")
  .action(async (options: { regenerate?: boolean }) => {
    try {
      const cwd = process.cwd();
      const intentPath = resolve(cwd, ".fathom", "intent.yaml");

      if (!existsSync(intentPath)) {
        warnAction(
          "No project intent found",
          "Intent file missing at .fathom/intent.yaml",
          "Run `fathom go` to set up a project with intent capture",
        );
        return;
      }

      const { loadIntent } = await import("@fathom/intent");
      const intent = await loadIntent(cwd);

      if (options.regenerate) {
        // Regenerate projections
        const { writeProjections } = await import("@fathom/projections");
        const files = await writeProjections(cwd, intent);
        console.log(chalk.green(`\n  ✓ Regenerated ${files.length} projection files from intent`));
        for (const f of files) {
          console.log(chalk.dim(`    ${f}`));
        }
        console.log();
        return;
      }

      // Show intent summary
      console.log(chalk.bold(`\n  Fathom — Project Intent`));
      console.log(chalk.dim("  " + "─".repeat(40)));
      console.log(`  Project:     ${intent.project}`);
      console.log(`  Description: ${intent.description}`);
      if (intent.audience) {
        console.log(`  Audience:    ${intent.audience}`);
      }

      if (intent.values.length > 0) {
        console.log(chalk.bold("\n  Values:"));
        for (const v of intent.values) {
          const pri = v.priority === "critical" ? chalk.red(v.priority)
            : v.priority === "high" ? chalk.yellow(v.priority)
            : chalk.dim(v.priority);
          console.log(`    ${v.name} (${pri}) — ${v.description}`);
          if (v.details) {
            console.log(chalk.dim(`      ${v.details}`));
          }
        }
      }

      if (intent.budget) {
        console.log(chalk.bold("\n  Budget:"));
        console.log(`    $${intent.budget.monthly_limit}/month (${intent.budget.currency})`);
        console.log(`    Alert at ${Math.round(intent.budget.alert_threshold * 100)}%`);
      }

      if (intent.guardrails) {
        const { resolveGuardrails } = await import("@fathom/intent");
        const resolved = resolveGuardrails(intent);
        const total = resolved.security.length + resolved.quality.length + resolved.process.length;
        console.log(chalk.bold(`\n  Guardrails: ${total} rules`));
        if (resolved.security.length > 0) console.log(`    Security: ${resolved.security.length} rules`);
        if (resolved.quality.length > 0) console.log(`    Quality:  ${resolved.quality.length} rules`);
        if (resolved.process.length > 0) console.log(`    Process:  ${resolved.process.length} rules`);
      }

      if (intent.tech) {
        if (intent.tech.stack.length > 0) {
          console.log(chalk.bold("\n  Stack:") + " " + intent.tech.stack.join(", "));
        }
        if (intent.tech.opinions.length > 0) {
          console.log(chalk.bold("  Opinions:") + " " + intent.tech.opinions.join(", "));
        }
      }

      // Projection status
      const projectionsDir = resolve(cwd, ".fathom", "projections");
      const hasProjections = existsSync(projectionsDir);
      console.log(chalk.bold("\n  Projections:") + (hasProjections ? chalk.green(" generated") : chalk.yellow(" not generated")));
      if (hasProjections) {
        const targets = ["claude/SKILL.md", "cursor/.cursorrules", "generic/SYSTEM_PROMPT.md"];
        for (const t of targets) {
          const exists = existsSync(resolve(projectionsDir, t));
          console.log(`    ${exists ? "✓" : "·"} ${t}`);
        }
      }

      console.log(chalk.dim("\n  Edit: .fathom/intent.yaml"));
      console.log(chalk.dim("  Regenerate: fathom project --regenerate\n"));
    } catch (err: unknown) {
      if (err && typeof err === "object" && "name" in err && (err as Error).name === "ExitPromptError") {
        console.log(chalk.dim("\n  Exiting.\n"));
        return;
      }
      throw err;
    }
  });
